/**
 * The agent rail.
 *
 * The rail on the right, mirroring the navigation rail on the left: it sits
 * directly on the window background with no border, fill or shadow, and the
 * product's card floats between the two. Giving this one a card of its own
 * would make the app read as three stacked panels instead of one surface.
 *
 * It opens BESIDE the content, never over it. That is the whole point: an agent
 * that operates your ledger is useless if it hides the ledger while you talk to
 * it, which is exactly what a modal would do.
 *
 * The conversation itself — sending, streaming, restoring, applying changes —
 * is `useAgentChat`. This file is the frame around it, and the full-page Agent
 * mode is a different frame around the very same hook.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Coins,
  History,
  Maximize2,
  MousePointer2,
  Plus,
  X,
} from 'lucide-react';
import { Button } from '@/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip';
import { useI18n } from '@/contexts/I18nContext';
import { useAppMode } from '@/contexts/AgentModeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useAgentChat } from './useAgentChat';
import { MessageList } from './MessageList';
import { Composer } from './Composer';
import { ConversationList } from './ConversationList';

/** Persisted as a percentage of the viewport — see the width state below. */
const WIDTH_KEY = 'railit_agent_width';
const MIN_PCT = 22;
const MAX_PCT = 78;
const DEFAULT_PCT = 30;
/** Drag past this and the rail becomes the full agent view. */
const AGENT_AT_PCT = 70;
/**
 * Where the magnet lets go again.
 *
 * Engaging and releasing on the same number means a hand resting on the line
 * flips the panel between its free width and the snap width several times a
 * second. The retreat has to be a deliberate few percent, not a tremor.
 */
const RELEASE_AT_PCT = 66;
/** The width the magnet holds it at: past the free range, so the jump is seen. */
const SNAP_PCT = MAX_PCT;
/**
 * What the rail drops back to after a trip through agent mode.
 *
 * The gesture leaves the width at ~70%. Coming back to a rail still pinned
 * there would put the ledger in a strip too narrow to read, which is the
 * opposite of what a rail beside the content is for.
 */
const RESET_PCT = 45;
/**
 * A percentage alone is not a width.
 *
 * 22% is a comfortable panel on a 27" monitor and 225px on a 1024px window —
 * narrower than the composer's own row of controls, so the model picker and
 * the send button collide. And the page beside it has to stay readable, or
 * "the agent opens beside your work" is a slogan.
 */
const MIN_PX = 300;
const CONTENT_MIN_PX = 360;

/**
 * The resize range for a window this wide, in percent.
 *
 * The ceiling never drops below AGENT_AT_PCT: at 1280px the content floor on
 * its own would put the top of the range at 68%, and the drag that enters agent
 * mode would be unreachable on the most common laptop there is. Past the
 * threshold the floor stops applying anyway — the snap is a preview of a mode
 * with no page beside it at all.
 */
function range(vw: number) {
  const hi = Math.max(AGENT_AT_PCT, Math.min(MAX_PCT, ((vw - CONTENT_MIN_PX) / vw) * 100));
  const lo = Math.min(Math.max(MIN_PCT, (MIN_PX / vw) * 100), hi);
  return { lo, hi };
}

function clampPct(pct: number, vw: number = window.innerWidth): number {
  const { lo, hi } = range(vw);
  return Math.min(hi, Math.max(lo, pct));
}

export function AgentPanel({
  open,
  onOpenChange,
  handoff = false,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /**
   * The rail is opening because a conversation was handed to it, rather than
   * because somebody asked for an empty panel. Changes how it arrives — see
   * the inner wrapper, and the effect in layout/Shell.tsx that sets this.
   */
  handoff?: boolean;
}) {
  const { t, lang, dir } = useI18n();
  const { org } = useAuth();
  const ar = lang === 'ar';
  const locale = ar ? 'ar-BH-u-nu-latn' : 'en';

  const chat = useAgentChat();
  const [draft, setDraft] = useState('');
  /* The rail has one width now. "Expand" means Agent mode, so the
     in-between size it used to toggle no longer has a reason to exist. */
  const { setMode } = useAppMode();

  /*
    THE PANEL'S WIDTH IS THE MODE.

    It had one fixed width and a button that jumped to the full-page view. Two
    controls for one spatial idea: how much of the screen is the conversation.
    Now you drag the edge, and if you drag it past most of the window it
    becomes agent mode — which is the same gesture, carried to its end.

    Stored as a percentage of the viewport rather than pixels so a laptop and an
    external monitor do not disagree about what "half" means.

    Past AGENT_AT_PCT the drag becomes MAGNETIC: the panel leaves the pointer
    and jumps to SNAP_PCT, and a label says what letting go will do. It used to
    switch modes on release with no warning at all, so the whole screen changed
    underneath somebody who thought they were resizing a panel. `widthPct` keeps
    following the pointer while that happens — the magnet holds the RENDERED
    width, not the value — which is what makes dragging back out of it exact
    rather than a second gesture you have to negotiate.
  */
  const [widthPct, setWidthPct] = useState<number>(() => {
    const saved = Number(localStorage.getItem(WIDTH_KEY));
    return clampPct(Number.isFinite(saved) && saved > 0 ? saved : DEFAULT_PCT);
  });
  const [dragging, setDragging] = useState(false);
  const [armed, setArmed] = useState(false);
  const isMobile = useIsMobile();

  /*
    Three mirrors of state, for one reason: the pointer listeners are attached
    once, in an effect, and a handler only ever sees the values its closure was
    built with. A magnet consulting a stale `armed` releases into the wrong
    branch — the panel snaps back to 30% AND switches to agent mode. The React
    state next to each ref exists solely so rendering reacts.
  */
  const draggingRef = useRef(false);
  const armedRef = useRef(false);
  const widthRef = useRef(widthPct);

  const arm = useCallback((next: boolean) => {
    if (armedRef.current === next) return;
    armedRef.current = next;
    setArmed(next);
  }, []);

  useEffect(() => {
    widthRef.current = widthPct;
    if (!draggingRef.current) localStorage.setItem(WIDTH_KEY, String(Math.round(widthPct)));
  }, [widthPct]);

  /*
    Handing the conversation to the full view. The rail closes on its way out,
    because the same thread rendered twice on one screen is not two
    conversations — it is the bug people report as "it duplicated my chat".
  */
  const toAgentMode = useCallback(() => {
    arm(false);
    onOpenChange(false);
    setMode('agent');
  }, [arm, onOpenChange, setMode]);

  /** The snap gets there through a width, so it has to leave a sane one behind. */
  const snapToAgentMode = useCallback(() => {
    setWidthPct(clampPct(RESET_PCT));
    toAgentMode();
  }, [toAgentMode]);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      if (!draggingRef.current) return;
      /* The panel is on the inline-end edge, so the width is the distance from
         the far edge — mirrored under RTL, where "far" is the other side. */
      const pct = clampPct(
        dir === 'rtl'
          ? (e.clientX / window.innerWidth) * 100
          : ((window.innerWidth - e.clientX) / window.innerWidth) * 100,
      );
      setWidthPct(pct);
      arm(pct >= (armedRef.current ? RELEASE_AT_PCT : AGENT_AT_PCT));
    };
    const up = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      /* Released inside the magnet: that is the request for the full view, and
         honouring it as "a very wide rail" would be pedantic. */
      if (armedRef.current) {
        snapToAgentMode();
        return;
      }
      localStorage.setItem(WIDTH_KEY, String(Math.round(widthRef.current)));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [arm, dir, snapToAgentMode]);

  /*
    The stored value is a percentage, but the limits it has to satisfy are in
    pixels — so a window that changes size can invalidate a width nobody
    touched. Drag the rail to the ceiling on a 1920px monitor, close the lid,
    and 71% of 1280px leaves the page behind it under 360px.
  */
  useEffect(() => {
    const onResize = () => setWidthPct((w) => clampPct(w));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /** What is on screen. The magnet holds this; `widthPct` stays with the pointer. */
  const shownPct = armed ? SNAP_PCT : widthPct;

  const [showHistory, setShowHistory] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  /* Closing drops everything transient. A magnet left engaged on a closed
     panel is waiting to reopen it at the snap width, showing a label about a
     gesture that ended minutes ago. */
  useEffect(() => {
    if (!open) {
      setShowHistory(false);
      arm(false);
    }
  }, [arm, open]);

  const submit = () => {
    const text = draft;
    setDraft('');
    void chat.send(text);
  };

  /**
   * Mounted whether open or not.
   *
   * `return null` when closed is why there was no closing animation — an
   * unmounted element cannot transition. Instead the panel keeps its place in
   * the flex row and animates its WIDTH to zero, which is also what makes the
   * content card grow back smoothly rather than snapping: it is a flex sibling,
   * so it follows this width for free.
   *
   * While closed it is inert — `pointer-events-none`, `aria-hidden`, and
   * `invisible` after the transition so nothing inside stays tabbable.
   */
  return (
    <aside
      // A sibling of the content card, not a child — see layout/Shell.tsx.
      data-testid="agent-panel"
      aria-hidden={!open}
      // React 19 types `inert` as a boolean; older DOM typings want the string.
      inert={!open || undefined}
      className={cn(
        // NOT a card. The left rail sits directly on the window background with
        // no border, fill or shadow, and this is the rail on the other side —
        // giving it a card would make the app read as three panels rather than
        // one surface with the product floating in the middle of it.
        'relative z-30 flex shrink-0 flex-col overflow-hidden text-sidebar-foreground',
        // The easing is deliberately asymmetric: a slower, decelerating open so
        // the eye can follow the panel arriving, and a quicker close because a
        // leisurely exit from something you just dismissed reads as lag.
        'motion-safe:transition-[width,opacity] motion-reduce:transition-none',
        open
          ? cn(
              'opacity-100 ease-[cubic-bezier(0.16,1,0.3,1)]',
              /* While the pointer owns the width, easing is lag: the edge has
                 to sit under the finger, not catch up to it 300ms later. The
                 magnet is the exception — that jump is the feedback, so it gets
                 a short ease of its own, and letting go of it is instant again
                 so the escape reads as the magnet releasing rather than as one
                 more animation to wait out. */
              dragging ? (armed ? 'duration-150' : 'duration-0') : 'duration-300',
            )
          : 'pointer-events-none invisible w-0 opacity-0 duration-200 ease-[cubic-bezier(0.4,0,1,1)]',

        // Engaged: a wash the colour of the action, so the region that is about
        // to become the whole screen is the region that is lit.
        armed && 'bg-primary/[0.045]',

        // On a narrow window there is no room to sit beside the content, so it
        // covers it — and there it DOES need a surface of its own.
        open &&
          'max-md:fixed max-md:inset-2 max-md:z-50 max-md:w-auto max-md:rounded-xl max-md:border max-md:bg-sidebar max-md:shadow-[var(--elev-3)]',
      )}
      aria-label={t('agent')}
      /*
        No inline width below md. An inline style beats `max-md:w-auto`, so the
        full-screen overlay was rendering as a 30%-wide sliver pinned by
        `inset-2` — the phone layout looked like a bug in the desktop one.
      */
      style={open && !isMobile ? { width: `${shownPct}%` } : undefined}
    >
      {/*
        The drag handle. Two pixels wide and eight to grab, on the inline-start
        edge because that is the boundary between the page and the panel.
        Keyboard users get the same reach through the arrow keys — a resize
        that only exists for a mouse is a resize half the people cannot make,
        and that includes the magnet: arrow to the threshold, Enter to go
        through it, Escape to back out.

        Not rendered at all below md, where the panel is an overlay filling the
        window: there is nothing to resize it against. `max-md:hidden` stays as
        well, because `useIsMobile` only knows the answer after its first
        effect and the handle would otherwise flash on the opening frame.
      */}
      {open && !isMobile ? (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('agent_resize')}
          aria-valuenow={Math.round(shownPct)}
          aria-valuemin={MIN_PCT}
          aria-valuemax={MAX_PCT}
          tabIndex={0}
          onPointerDown={(e) => {
            e.preventDefault();
            draggingRef.current = true;
            setDragging(true);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
          }}
          onKeyDown={(e) => {
            if (armed && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              snapToAgentMode();
              return;
            }
            /* Escape has to leave the width below the line as well as disarm.
               Disarming alone parks the panel exactly on the threshold, where
               the next widen keypress arms it again and the escape reads as
               having been ignored. */
            if (armed && e.key === 'Escape') {
              e.preventDefault();
              arm(false);
              setWidthPct(clampPct(RELEASE_AT_PCT - 1));
              return;
            }
            const step = e.shiftKey ? 8 : 3;
            const back = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
            const fwd = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
            let next: number;
            if (e.key === back) next = clampPct(widthPct + step);
            else if (e.key === fwd) next = clampPct(widthPct - step);
            else return;
            e.preventDefault();
            setWidthPct(next);
            /* No hysteresis on this path. A key press cannot jitter across the
               line the way a resting hand can, and a magnet that takes two
               presses to let go reads as a stuck key. */
            arm(next >= AGENT_AT_PCT);
          }}
          className={cn(
            'absolute inset-y-0 start-0 z-40 w-2 cursor-col-resize max-md:hidden',
            'after:absolute after:inset-y-0 after:start-[3px] after:w-px after:bg-border/60',
            'hover:after:bg-foreground/30 focus-visible:outline-none focus-visible:after:bg-ring',
            'motion-safe:after:transition-colors',
            // Engaged: the hairline seam becomes a lit edge. The panel has left
            // the pointer by then, so the handle is the one thing still under
            // it that can say the gesture changed.
            armed && 'after:w-0.5 after:bg-primary hover:after:bg-primary',
          )}
        />
      ) : null}

      {/*
        What releasing will do, said before it happens.

        The mode used to change on pointer-up with no warning: the panel was
        wide, then the entire screen was a conversation. A magnet you cannot
        see is just a bug with good intentions.
      */}
      {armed ? (
        <div
          role="status"
          className={cn(
            'pointer-events-none absolute inset-x-0 top-16 z-40 flex justify-center px-4',
            'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1',
          )}
        >
          <span className="rounded-full border border-primary/25 bg-background/95 px-3 py-1 text-[11.5px] font-medium shadow-[var(--elev-2)] backdrop-blur-sm">
            {dragging ? t('agent_snap_release') : t('agent_snap_confirm')}
          </span>
        </div>
      ) : null}
      {/*
        The contents are given a fixed width and slid in, rather than laid out
        inside a box that is still growing — otherwise every label reflows on
        each frame of the opening animation, which reads as a stutter.
      */}
      <div
        className={cn(
          'flex h-full flex-col w-full',
          'max-md:w-auto',
          'motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.16,1,0.3,1)]',
          open
            ? 'translate-x-0'
            : /*
                A handoff comes from the OTHER side.

                The offset is what tells you where the panel came from, and on a
                handoff the conversation was filling the card a frame ago —
                sliding it in from the window edge says it arrived from outside
                the app, so the card narrowing and the transcript sliding read as
                two unrelated things happening at once. Starting it a few pixels
                toward the content and letting it settle makes the two halves of
                the motion agree: the conversation moved inwards, into the rail.
              */
              handoff
              ? '-translate-x-4 rtl:translate-x-4'
              : 'translate-x-6 rtl:-translate-x-6',
        )}
      >
        {/* ---- chrome ---- */}
        <header className="flex h-14 shrink-0 items-center gap-1 px-3">
          <span className="flex items-center gap-2 text-[13.5px] font-medium">
            <MousePointer2 className="size-4" aria-hidden />
            {t('agent')}
          </span>

          {/*
            Stays after the turn ends, on purpose. A meter that clears itself
            when the answer arrives disappears at exactly the moment somebody
            wants to know what that answer cost.
          */}
          {chat.sessionCredits > 0 || chat.pending ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'ms-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5',
                    'text-[11px] font-medium tabular-nums',
                    chat.pending ? 'bg-muted text-muted-foreground' : 'text-muted-foreground',
                  )}
                >
                  <Coins className="size-3" aria-hidden />
                  {chat.sessionCredits.toLocaleString(locale)}
                  {chat.pending ? <span className="animate-pulse">…</span> : null}
                </span>
              </TooltipTrigger>
              <TooltipContent>{t('agent_session_cost')}</TooltipContent>
            </Tooltip>
          ) : null}

          <div className="ms-auto flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('agent_new')}
              onClick={() => {
                chat.newConversation();
                setShowHistory(false);
                setDraft('');
              }}
            >
              <Plus className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('agent_history')}
              data-testid="agent-history"
              data-active={showHistory || undefined}
              className="data-[active]:bg-accent"
              onClick={() => setShowHistory((v) => !v)}
            >
              <History className="size-4" />
            </Button>
            {/*
              Expanding goes ALL the way — into Agent mode, not a wider rail.

              The button widened the panel by a couple of hundred pixels, which
              is neither one thing nor the other: still a sidebar, still
              cramped, and the dashboard beside it now squeezed. Somebody who
              reaches for "expand" in the middle of a conversation wants the
              conversation to be the screen, and that surface already exists.
              The panel closes as it goes, because leaving a rail open behind
              the full-page view would put the same conversation on screen
              twice.
            */}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('agent_expand_full')}
              title={t('agent_expand_full')}
              className="max-md:hidden"
              onClick={toAgentMode}
            >
              <Maximize2 className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('close')}
              onClick={() => onOpenChange(false)}
            >
              <X className="size-4" />
            </Button>
          </div>
        </header>

        {showHistory ? (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-3">
            <ConversationList
              activeId={chat.conversationId}
              onPick={(id) => {
                void chat.openConversation(id);
                setShowHistory(false);
              }}
            />
          </div>
        ) : (
          <>
            <div
              ref={scrollRef}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4"
            >
              {/*
                `loadingThread`, not just an empty transcript.

                The rail re-reads the thread from the server every time it
                mounts, so on the way back from agent mode there is a beat where
                the messages have not arrived yet. Keyed on length alone, that
                beat renders "How can I help?" — the app cheerfully greeting
                somebody in the middle of a conversation, which is the exact
                thing that reads as having lost it. AgentWorkspace guards the
                same way.
              */}
              {chat.messages.length === 0 && !chat.loadingThread ? (
                <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                  <p className="text-sm font-medium">{t('agent_greeting')}</p>
                  {/*
                    Which company the agent is about to act in, stated before
                    the first message rather than after the first mistake. One
                    account can hold several workspaces, and "create an invoice"
                    is a different instruction in each.
                  */}
                  {org ? (
                    <p className="mt-1.5 text-[11.5px] text-muted-foreground">
                      {t('agent_scope').replace('{org}', (ar ? org.name_ar : null) ?? org.name)}
                    </p>
                  ) : null}
                </div>
              ) : (
                <MessageList
                  messages={chat.messages}
                  decided={chat.decided}
                  onDecide={chat.decide}
                  error={chat.error}
                />
              )}
            </div>

            <div className="shrink-0 p-3">
              <Composer
                value={draft}
                onChange={setDraft}
                onSend={submit}
                onStop={chat.stop}
                pending={chat.pending}
                files={chat.files}
                onAddFiles={chat.addFiles}
                onRemoveFile={chat.removeFile}
                model={chat.model}
                onModelChange={chat.setModel}
                thinking={chat.thinking}
                onThinkingChange={chat.setThinking}
                autoFocus={open}
              />
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
